import { useState } from "react";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const REASONS = [
  { value: "ai_quality", label: "AI quality" },
  { value: "incorrect_answers", label: "Incorrect answers" },
  { value: "booking_problems", label: "Booking problems" },
  { value: "voice_delays", label: "Voice or response delays" },
  { value: "too_expensive", label: "Too expensive" },
  { value: "not_using_enough", label: "Not using it enough" },
  { value: "other", label: "Other" },
];

export function CancelSubscriptionDialog({ open, onClose }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);

  const doCancel = async () => {
    const r = await api.post("/billing/cancel");
    return r.data;
  };

  const sendFeedback = async (requestHelp) => {
    if (!reason) return;
    await api.post("/billing/cancel-feedback", {
      reason, details: details.trim(), request_help: requestHelp,
    });
  };

  const onRequestHelp = async () => {
    if (!reason) { toast.error("Please pick a reason first — it helps us fix the right thing."); return; }
    setBusy("help");
    try {
      await sendFeedback(true);
      toast.success("Support request sent — we've emailed you a confirmation and our team will reach out shortly.");
      onClose();
    } catch (e) { toast.error(errMsg(e, "Could not send the request — please try again")); }
    finally { setBusy(null); }
  };

  const onContinueToCancel = async () => {
    setBusy("cancel");
    try {
      if (reason) await sendFeedback(false);
      const r = await doCancel();
      setResult(r);
      window.setTimeout(() => window.location.reload(), 4000);
    } catch (e) {
      toast.error(errMsg(e, "Could not cancel — please try again"));
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="cancel-dialog">
        {result ? (
          <div className="py-6 text-center" data-testid="cancel-done">
            <DialogHeader><DialogTitle>Your subscription is cancelled</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600 mt-3">
              {result.refund_amount != null
                ? <>Refunded <b>${result.refund_amount.toFixed(2)} CAD</b> — {result.refund_fraction === 1 ? "a full refund, since you used half your minutes or less this month" : "a half refund, based on this month's usage"} ({result.percent_used}% used).</>
                : "No payment this period to refund."}
            </p>
            <p className="text-xs text-slate-400 mt-2">This page will refresh…</p>
          </div>
        ) : (<>
        <DialogHeader>
          <DialogTitle>Before you go — what's the main reason?</DialogTitle>
          <DialogDescription>
            One quick question. Cancelling itself stays one click away either way.
          </DialogDescription>
        </DialogHeader>
        <RadioGroup value={reason} onValueChange={setReason} className="mt-2 space-y-2" data-testid="cancel-reasons">
          {REASONS.map(r => (
            <div key={r.value} className="flex items-center gap-2.5">
              <RadioGroupItem value={r.value} id={`reason-${r.value}`} data-testid={`reason-${r.value}`} />
              <Label htmlFor={`reason-${r.value}`} className="font-normal cursor-pointer">{r.label}</Label>
            </div>
          ))}
        </RadioGroup>
        <Textarea data-testid="cancel-details" value={details} onChange={(e) => setDetails(e.target.value)}
          placeholder="Anything else we should know? (optional)" className="mt-3 min-h-[72px]" />
        <div className="mt-4 flex flex-col gap-2">
          <Button data-testid="cancel-request-help-btn" variant="outline" disabled={!!busy} onClick={onRequestHelp}
            className="rounded-xl border-blue-200 text-blue-700 hover:bg-blue-50 disabled:text-blue-700 disabled:opacity-60">
            {busy === "help" ? "Sending…" : "Request help fixing this"}
          </Button>
          <Button data-testid="cancel-continue-btn" disabled={!!busy} onClick={onContinueToCancel}
            className="rounded-xl bg-red-600 hover:bg-red-700">
            {busy === "cancel" ? "Cancelling…" : "Cancel my subscription"}
          </Button>
          <button type="button" data-testid="cancel-skip-btn" disabled={!!busy} onClick={onContinueToCancel}
            className="text-xs text-slate-400 hover:text-slate-600 underline mt-1">
            Skip — cancel now
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2" data-testid="cancel-refund-policy">
          Cancellation is immediate. Refund policy: used half your included minutes or less this month → full refund of this month's payment; more than half → half refund. Refunds land back on your card in 5–10 days.
        </p>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
